import React from 'react';
import UserContextInterface, { UserAction } from "@app/Interface/UserContextInterface";
import { userContextReducer } from "@app/Context/Reducer/UserContextReducer";
import UserModel from '@app/Model/UserModel';
import User from '@app/Interface/UserInterface';
import AppSettings from '@app/utils/AppSettings';

const initialUser = new UserModel();
initialUser.iri = "/api/users/0";
initialUser.id = 0;
initialUser.firstName = "Initial";
initialUser.lastName = "User";
initialUser.username = "iuser@example.com";
initialUser.email = "iuser@example.com";

const userInitialState: UserContextInterface = {
    user: initialUser
}

const UserContext = React.createContext<{
    userState: UserContextInterface;
    userDispatch: React.Dispatch<UserAction>;
}>({
    userState: userInitialState,
    userDispatch: () => null
});

/**
 * Updated to load data from localStorage.
 * see: https://dev.to/bigaru/creating-persistent-synchronized-global-store-using-react-hooks-in-typescript-209a#persistence
 * 
 * @param children
 * @returns 
 */
const UserProvider: React.FC = ({ children }) => {

    const STORAGE_KEY = "USER_DATA";

    // 3rd function argument tries to get data from local storage
    const [userState, userDispatch] = React.useReducer(userContextReducer, userInitialState, (state) => {

        const persistedData = localStorage.getItem(STORAGE_KEY);

        const persistedUser: User = persistedData ? JSON.parse(persistedData) : userInitialState.user;

        state.user = persistedUser;

        return { ...state };

    });

    // save state on every change
    React.useEffect(() => {

        if (userState.user.id) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(userState.user));
            localStorage.setItem(AppSettings.REMEMBER_FPU_STORAGE_KEY, JSON.stringify(userState.user.currentFpu));
        }

        // added userState.user.currentFpuId since it appears that querystring fpu doesn't trigger a state change on high
        // level properties. So, we force a check currentFpuId
    }, [userState.user, userState.user.id, userState.user.currentFpuId]);

    // use the newest data on every localStorage change 
    // by adding a listener
    React.useEffect(() => {

        // see: https://developer.mozilla.org/en-US/docs/Web/API/StorageEvent
        window.addEventListener("storage", (event: StorageEvent) => {

            if (event.key === STORAGE_KEY) {

                const persistedData = localStorage.getItem(STORAGE_KEY);

                const newUserData: User = persistedData ? JSON.parse(persistedData) : null;

                if (newUserData) {
                    userDispatch({
                        type: "SYNC_USER",
                        payload: newUserData
                    });
                }

            }

        });

    }, []);

    return (
        <UserContext.Provider value={{ userState, userDispatch }}>
            {children}
        </UserContext.Provider>
    );

}

export { UserProvider, UserContext }